import Swiper from 'swiper/bundle';
import TariffRenderer from './tariff-renderer.es6';
import FormValidationService from './form-validation-service.es6';

// Selectors (LP minimal)
const SELECTORS = {
    FORM: '#change-tariff-form',
    FORM_CONTAINER: '.form-box',
    FORM_WRAPPER: '.section-change-tariff .wrapper',
    LOADER: '.section-change-tariff #loader',
    SUBMIT_BUTTON: '#change-tariff-form [type="submit"]',
    API_ERROR_MESSAGE: '.section-change-tariff .api-error-message',

    CURRENT_TARIFF_BOX: '.tariff-card.-current',
    NEW_TARIFF_BOX: '.tariff-card.-new',
    POPUP_WRAPPER: '.section-change-tariff .popup-wrapper',

    NEW_TARIFF_CTA: '.tariff-card.-new .btn-cta',
    API_ERROR_MESSAGE_GLOBAL: '.section-change-tariff .api-error-message--global',
    API_ERROR_MESSAGE_SUBMIT: '.section-change-tariff .api-error-message--submit',

    TARIFF_SWIPER: '.section-change-tariff .tariff-cards.swiper',
    TARIFF_TABS: '.section-change-tariff .tariff-tabs',
    TARIFF_TAB: '.section-change-tariff .tariff-tab'
};

// Constants
const COMPARISON = {
    NEW_TARIFF_ID: 131,
};

const REQUIRED_URL_PARAMS = ['VNR', 'BP', 'KWH'];

class ChangeTariffApiService {
    constructor() {
        // Internal state
        this._loading = false;
        this.onLoadingChange = null;
        this._abort = false;

        // Config
        // Absolute path (from myajax, localized in functions.php via get_stylesheet_directory_uri())
        // rather than a relative "../" path - a relative path breaks once the current URL is
        // nested more than one segment deep, since "../" only pops a single path segment
        // instead of resolving back to the site root.
        this.proxyUrl = (typeof myajax !== 'undefined' && myajax.proxy_url) || '../wp-content/themes/gruuun-theme/api-proxi.php';

        // URL params
        this.urlParams = this.readUrlParams();
        this.contractNumberValue = this.urlParams.VNR || '';
        this.businessPartnerNumberValue = this.urlParams.BP || '';
        this.consumption = this.urlParams.KWH || '';

        // Data snapshots
        this.contractData = null;
        this.bpData = null;
        this.currentAddress = null;

        // Current tariff meta (resolved later via getTariffApplication)
        this.currentTariffName = '';
        this.currentTariffId = '';

        // Calculation cache
        this.totalCostPerMonth = null;

        this.tariffCardsSwiper = null;
        this._tariffSliderBound = false;

        this.formValidation = new FormValidationService({
            selectors: {
                FORM: SELECTORS.FORM,
                FIELD_CONTAINER: '.nf-field-container',
                ALL_INPUTS: '.ninja-forms-field:not(.select2-hidden-accessible)',
                NINJA_FORMS_FIELD: '.ninja-forms-field',
                DATE_ICON: '.date-icon',
                DATE_INPUT: '.date-container .input',
                DATE_LABEL: '.date-container label'
            },
            flatpickrItems: [
                { selector: '.date-container .datepicker', options: {} },
            ],
            rules: {
                dateOfBirth: {
                    dateOfBirthRange: true
                },
            },
            messages: {
                // локальні оверрайди
            },
            onValidSubmit: (form, event) => this.handleFormSubmit(event),
        });

        this.formValidation.init();

        this.initializeEventListeners();
        this.preloadComparison();
    }

    // -----------------------------
    // Loading state helper
    // -----------------------------
    get loading() {
        return this._loading;
    }

    set loading(value) {
        if (this._loading !== value) {
            this._loading = value;
            if (typeof this.onLoadingChange === "function") {
                this.onLoadingChange(value);
            }
        }
    }

    // -----------------------------
    // URL
    // -----------------------------
    readUrlParams() {
        const sp = new URLSearchParams(window.location.search);

        const params = {
            VNR: sp.get('VNR') || '',
            BP: sp.get('BP') || '',
            KWH: sp.get('KWH') || '',
        };

        Object.keys(params).forEach(k => {
            if (typeof params[k] === 'string') params[k] = params[k].trim();
        });

        return params;
    }

    assertUrlParamsOrAbort(params) {
        const missing = REQUIRED_URL_PARAMS.filter(k => !params[k]);

        if (missing.length) {
            // this.hideLoader();
            // console.error('Missing URL params:', missing);
            this.renderApiError('Leider konnten wir keine passenden Daten zu Deinem Tarif finden. Bitte öffne den Link aus der E-Mail erneut oder kontaktiere den Kundenservice');

            this._abort = true;
            return false;
        }

        return true;
    }

    isMobileTariffView() {
        return window.matchMedia('(max-width: 949.5px)').matches;
    }

    shouldUseTariffSwiper() {
        return window.matchMedia('(max-width: 949.5px)').matches;
    }

    getMostVisibleTariffType(swiper) {
        if (!swiper || !swiper.el) return 'new';

        const containerRect = swiper.el.getBoundingClientRect();
        let bestType = 'new';
        let bestVisible = -1;

        swiper.slides.forEach((slide) => {
            const type = slide?.dataset?.tariffType;
            if (!type) return;

            const r = slide.getBoundingClientRect();

            const visibleWidth = Math.max(
                0,
                Math.min(r.right, containerRect.right) - Math.max(r.left, containerRect.left)
            );

            if (visibleWidth > bestVisible) {
                bestVisible = visibleWidth;
                bestType = type;
            }
        });

        return bestType;
    }

    getTariffSlideIndexByType(swiper, type) {
        if (!swiper) return 0;

        for (let i = 0; i < swiper.slides.length; i++) {
            const t = swiper.slides[i]?.dataset?.tariffType;
            if (t === type) return i;
        }

        return 0;
    }

    applyTariffDesktopLock(swiper) {
        if (!swiper) return;

        const isMobile = this.isMobileTariffView();

        // Lock swipe on desktop, allow on mobile
        swiper.allowTouchMove = isMobile;

        const idxNew = this.getTariffSlideIndexByType(swiper, 'new');
        const idxCurrent = this.getTariffSlideIndexByType(swiper, 'current');

        // Reverse active logic by breakpoint
        if (isMobile) {
            swiper.slideTo(idxNew, 0);
        } else {
            swiper.slideTo(idxCurrent, 0);
        }

        $(swiper.el).toggleClass('-locked-desktop', !isMobile);

        swiper.update();
    }

    updateTariffTabsState(swiper) {
        const $tabs = $(SELECTORS.TARIFF_TAB);
        if (!$tabs.length || !swiper) return;

        const activeType = this.getMostVisibleTariffType(swiper);

        $tabs.removeClass('-active').attr('aria-selected', 'false');
        $tabs
            .filter(`[data-tariff="${activeType}"]`)
            .addClass('-active')
            .attr('aria-selected', 'true');
    }

    initOrDestroyTariffCardsSlider() {
        const el = document.querySelector(SELECTORS.TARIFF_SWIPER);
        if (!el) return;

        const shouldInit = this.shouldUseTariffSwiper();

        // INIT
        if (shouldInit && !this.tariffCardsSwiper) {
            this.tariffCardsSwiper = new Swiper(el, {
                slidesPerView: 'auto',
                spaceBetween: 16,
                speed: 300,
                resistanceRatio: 0.85,
                watchOverflow: true,
                observer: true,
                observeParents: true,

                on: {
                    init: (swiper) => {
                        this.bindTariffTabs(swiper);

                        // Default active for mobile: NEW first
                        const idxNew = this.getTariffSlideIndexByType(swiper, 'new');
                        swiper.slideTo(idxNew, 0);

                        this.updateTariffTabsState(swiper);

                        // Small deferred update helps when widths are CSS-driven
                        setTimeout(() => {
                            swiper.update();
                            this.updateTariffTabsState(swiper);
                        }, 0);
                    },
                    slideChange: (swiper) => {
                        this.updateTariffTabsState(swiper);
                    },
                    transitionEnd: (swiper) => {
                        this.updateTariffTabsState(swiper);
                    },
                    resize: (swiper) => {
                        this.updateTariffTabsState(swiper);
                    }
                }
            });

            return;
        }

        // DESTROY
        if (!shouldInit && this.tariffCardsSwiper) {
            this.tariffCardsSwiper.destroy(true, true);
            this.tariffCardsSwiper = null;

            // Ensure tabs state doesn't lie on desktop
            const $tabs = $(SELECTORS.TARIFF_TAB);
            $tabs.removeClass('-active').attr('aria-selected', 'false');

            return;
        }

        // REFRESH
        if (shouldInit && this.tariffCardsSwiper) {
            this.tariffCardsSwiper.update();
            this.updateTariffTabsState(this.tariffCardsSwiper);
        }
    }


    bindTariffTabs(swiper) {
        if (this._tariffSliderBound) return;

        $(document).on('click.changeTariffTabs', SELECTORS.TARIFF_TAB, (e) => {
            e.preventDefault();

            if (!this.tariffCardsSwiper) return;

            const type = $(e.currentTarget).data('tariff');
            const idx = this.getTariffSlideIndexByType(this.tariffCardsSwiper, type);

            this.tariffCardsSwiper.slideTo(idx, 300);

            // Fast UI feedback
            this.updateTariffTabsState(this.tariffCardsSwiper);
        });

        this._tariffSliderBound = true;
    }

    createTariffCardsSwiper(el) {
        this.tariffCardsSwiper = new Swiper(el, {
            slidesPerView: 'auto',
            spaceBetween: 16,
            speed: 300,
            resistanceRatio: 0.85,
            watchOverflow: true,
            freeMode: false,
            on: {
                init: (swiper) => {
                    // Mobile default: NEW first
                    const idxNew = this.getTariffSlideIndexByType(swiper, 'new');
                    swiper.slideTo(idxNew, 0);

                    this.bindTariffTabs(swiper);
                    this.updateTariffTabsState(swiper);
                },
                slideChange: (swiper) => {
                    this.updateTariffTabsState(swiper);
                }
            }
        });
    }

    getActiveTariffType(swiper) {
        if (!swiper || !swiper.el) return 'current';

        // 1) Most reliable: DOM active class
        const activeEl = swiper.el.querySelector('.swiper-slide-active');
        const activeType = activeEl?.dataset?.tariffType;
        if (activeType) return activeType;

        // 2) Fallback: by index
        const idxSlide = swiper.slides?.[swiper.activeIndex];
        const idxType = idxSlide?.dataset?.tariffType;
        if (idxType) return idxType;

        // 3) Fallback: first visible
        const visibleEl = swiper.el.querySelector('.swiper-slide-visible');
        const visibleType = visibleEl?.dataset?.tariffType;
        if (visibleType) return visibleType;

        // 4) Last resort
        return 'current';
    }

    destroyTariffCardsSwiper() {
        if (!this.tariffCardsSwiper) return;

        this.tariffCardsSwiper.destroy(true, true);
        this.tariffCardsSwiper = null;

        // Optional: clear tabs state when slider is gone
        const $tabs = $(SELECTORS.TARIFF_TAB);
        if ($tabs.length) {
            $tabs.removeClass('-active').attr('aria-selected', 'false');
        }
    }

    updateTariffCardsSliderMode() {
        const el = document.querySelector(SELECTORS.TARIFF_SWIPER);
        if (!el) return;

        const isMobile = this.isMobileTariffView();

        if (isMobile) {
            if (!this.tariffCardsSwiper) {
                this.createTariffCardsSwiper(el);
            } else {
                this.tariffCardsSwiper.update();
                this.updateTariffTabsState(this.tariffCardsSwiper);
            }
        } else {
            // Desktop: no swiper at all
            this.destroyTariffCardsSwiper();
        }
    }

    // -----------------------------
    // Proxy
    // -----------------------------
    _requestProxy(type, body) {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${this.proxyUrl}?type=${type}`,
                method: 'POST',
                contentType: 'application/json',
                data: body ? JSON.stringify(body) : null,
                dataType: 'json'
            })
                .done(res => resolve(res))
                .fail((xhr, status, err) => reject(err || status));
        });
    }

    // -----------------------------
    // Filtered WP ajax endpoints
    // -----------------------------
    getFilteredContract(bpNumber, contractNumber) {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: myajax.url,
                method: 'POST',
                dataType: 'json',
                data: {
                    action: 'get_change_tariff_contract_data',
                    businessPartnerNumber: bpNumber,
                    contractNumber: contractNumber,
                    nonce: myajax.nonces.get_change_tariff_contract_data
                },
                success: (res) => {
                    if (res && res.success && res.data) resolve(res.data);
                    else reject(res?.data || 'Filtered contract error');
                },
                error: (xhr, status, err) => reject(err || status)
            });
        });
    }

    getDateOfBirthInputValue() {
        const $form = $(SELECTORS.FORM);
        const raw = ($form.find('[name="dateOfBirth"]').val() || '').toString().trim();
        if (!raw) return '';

        // Already ISO from flatpickr: YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return raw;
        }

        // Fallback for manual input: DD.MM.YYYY -> YYYY-MM-DD
        const m = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (m) {
            const day = String(parseInt(m[1], 10)).padStart(2, '0');
            const month = String(parseInt(m[2], 10)).padStart(2, '0');
            const year = String(parseInt(m[3], 10));
            return `${year}-${month}-${day}`;
        }

        return '';
    }

    getNewTariffStartDate() {
        // Rule:
        // Use 01.01.2026 until we reach that day.
        // From 01.01.2026 or later => current date + 1 day.
        // Important: build dates in UTC midnight to avoid timezone shift in toISOString()

        const now = new Date();

        const cutoverUtc = new Date(Date.UTC(2026, 0, 1, 0, 0, 0, 0));

        // Compare by "today" in UTC (not local)
        const todayUtc = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate(),
            0, 0, 0, 0
        ));

        if (todayUtc < cutoverUtc) {
            return cutoverUtc;
        }

        // tomorrow UTC midnight
        const tomorrowUtc = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() + 1,
            0, 0, 0, 0
        ));

        return tomorrowUtc;
    }

    normalizeSubmitErrorMessage(err) {
        if (!err) return 'Leider ist etwas schiefgelaufen. Bitte versuche es erneut.';

        if (err?.code === 'dob_mismatch') {
            return 'Geburtsdatum stimmt nicht mit Vertragsinhaber überein';
        }

        if (typeof err === 'string') {
            // Fallback heuristic for backend string errors
            if (err.toLowerCase().includes('dob')) {
                return 'Geburtsdatum stimmt nicht mit Vertragsinhaber überein';
            }
            return err;
        }

        return 'Leider ist etwas schiefgelaufen. Bitte versuche es erneut.';
    }

    getFilteredBusinessPartner(bpNumber, dateOfBirth = '') {
        return new Promise((resolve, reject) => {
            // dateOfBirth is required by backend
            if (!dateOfBirth) {
                reject('dateOfBirth is missed');
                return;
            }

            const payload = {
                action: 'get_change_tariff_business_partner',
                businessPartnerNumber: bpNumber,
                dateOfBirth: dateOfBirth, // expects milliseconds timestamp
                nonce: myajax.nonces.get_change_tariff_business_partner
            };

            $.ajax({
                url: myajax.url,
                method: 'POST',
                dataType: 'json',
                data: payload,
                success: (res) => {
                    if (res && res.success && res.data) {
                        resolve(res.data);
                        return;
                    }

                    const backendError = res?.data;

                    // Normalize DOB mismatch error coming from backend
                    if (
                        backendError === 'Geburtsdatum stimmt nicht mit Vertragsinhaber überein'
                    ) {
                        reject({ code: 'dob_mismatch', message: backendError });
                        return;
                    }

                    reject(backendError || 'Filtered BP error');
                },
                error: (xhr, status, err) => reject(err || status)
            });
        });
    }

    // -----------------------------
    // API calls
    // -----------------------------
    getTariffApplication(contractNumber) {
        return this._requestProxy('getTariffApplication', {
            contractNumber: contractNumber
        });
    }

    getPriceInformationByAddress(address, consumption) {
        const requestBody = {
            address: {
                city: address.city,
                postalCode: address.postalCode,
                street: address.street,
                housenumber: address.housnumber
            },
            consumption: Number(consumption),
            requestDate: new Date().toISOString(),
            section: "Electricity"
        };

        return this._requestProxy('getPriceInformation', requestBody);
    }

    // -----------------------------
    // UI renderers
    // -----------------------------

    renderTariffOverview({ currentTariffInfo, newTariffInfo, city, postalcode, consumption }) {
        const $this = this;

        function ajaxRequestAsPromise(requestData) {
            return new Promise((resolve, reject) => {
                $.ajax({
                    url: myajax.url,
                    method: 'POST',
                    data: requestData,
                    success(response) {
                        if (!response.success) return reject(new Error(response.data));
                        resolve(response);
                    },
                    error(jqXHR, textStatus, errorThrown) {
                        reject(new Error('AJAX error: ' + textStatus + ' - ' + errorThrown));
                    }
                });
            });
        }

        const fetchInfo = (tariffId) => ajaxRequestAsPromise({
            action: 'get_tariff_info',
            city,
            postalCode: postalcode,
            tariff_id: tariffId,
            nonce: myajax.nonces.get_tariff_info
        });

        // ✅ Helper for formula-based monthly cost
        const calcMonthlyByFormula = (tariffInfo) => {
            const t = tariffInfo?.tariff || {};
            const eg = Number(t.energyPriceGross || 0);
            const bf = Number(t.baseFeeGross || 0);
            const c = Number(consumption || 0);

            return (c * eg) / 12 + bf;
        };
        const getTotalCostPerYear = (tariffInfo) => {
            const t = tariffInfo?.priceCalculation || {};

            return t.totalCostPerMonth || 0;
        };

        return Promise.all([
            fetchInfo(currentTariffInfo.tariff.id),
            fetchInfo(newTariffInfo.tariff.id),
        ]).then(([currentResp, newResp]) => {


            const currentMonthlyFormula = calcMonthlyByFormula(currentTariffInfo);

            const newMonthlyFormula = (newResp.data.tariffType === "-successor")? calcMonthlyByFormula(newTariffInfo) : getTotalCostPerYear(newTariffInfo);

            const monthlyDiff = newMonthlyFormula - currentMonthlyFormula;

            const yearlyDiff = monthlyDiff * 12;

            const currentBundle = TariffRenderer.buildTariffBundle({
                idPrefix: 'current-',
                tariffInfo: currentTariffInfo,
                responseData: currentResp.data,
                consumption,
                showChooseButton: false,

                fakeButtonText: 'Dein aktiver Vertrag',
                forceBadgeText: 'Dein aktueller Tarif',
                forceSuccessorTariffOverview: true
            });

            const newBundle = TariffRenderer.buildTariffBundle({
                idPrefix: 'new-',
                tariffInfo: newTariffInfo,
                responseData: newResp.data,
                consumption,
                showChooseButton: false,

                fakeButtonText: 'Mein neuer Tarif',

                monthlyDiff,
                yearlyDiff,
                forceBadgeText: 'Dein neuer Tarif'
            });

            $('.tariff-card.-current').html(currentBundle.mainHtml);
            $('.tariff-card.-new').html(newBundle.mainHtml);

            $(SELECTORS.POPUP_WRAPPER).html(
                currentBundle.popupsHtml + newBundle.popupsHtml
            );

            $this.tariffOverviewUi = {
                current: currentBundle,
                new: newBundle
            };

        }).catch(err => {
            console.error(err);
            $('.tariff-card.-current, .tariff-card.-new').html(
                `<div class="error-wrapper text-3"><p>Etwas ist schiefgelaufen.</p></div>`
            );
        });
    }

    // -----------------------------
    // Tariff resolvers for overview
    // -----------------------------
    async resolveCurrentTariffInfo(consumption) {
        const application = await this.getTariffApplication(this.contractNumberValue);

        // Raw object from proxy / MBS
        if (!application || typeof application !== 'object') {
            throw new Error('Tariff application not found');
        }

        if (!application.tariffId) {
            throw new Error('Current tariff id is empty');
        }

        // Save for later if needed
        this.currentTariffId = String(application.tariffId);

        // Normalize to common tariffInfo structure
        const normalized = this.mapCurrentTariffApplication(application, consumption);

        return normalized;
    }

    async resolveNewTariffInfo(consumption) {
        const address = this.currentAddress;

        const priceRes = await this.getPriceInformationByAddress(address, consumption);
        const items = Array.isArray(priceRes?.priceInformation) ? priceRes.priceInformation : [];

        const target = items.find(pi => String(pi?.tariff?.id) === String(COMPARISON.NEW_TARIFF_ID));
        if (!target) throw new Error('Tariff 131 not found in getPriceInformation');

        const calc = target.priceCalculation || {};
        const tariff = target.tariff || {};

        const totalCostPerMonthRaw = Number(
            calc.totalCostPerMonth ?? tariff.totalCostPerMonth ?? 0
        );

        // Store for submit payload (rounded down whole Euro)
        this.totalCostPerMonth = Number.isFinite(totalCostPerMonthRaw)
            ? Math.floor(totalCostPerMonthRaw)
            : null;

        return target;
    }

    /**
     * Normalize getTariffApplication response to common tariffInfo shape.
     * application: raw object from MBS
     * consumption: yearly kWh
     */
    mapCurrentTariffApplication(application, consumption) {
        // Fallbacks and safe numbers
        const consumptionNum = Number(consumption || this.consumption || 0);

        const energyPriceNet   = Number(application.energyPriceNet   ?? 0);
        const energyPriceTax   = Number(application.energyPriceTax   ?? 0);
        const energyPriceGross = Number(application.energyPriceGross ?? 0);

        const baseFeeNet   = Number(application.baseFeeNet   ?? 0);
        const baseFeeTax   = Number(application.baseFeeTax   ?? 0);
        const baseFeeGross = Number(application.baseFeeGross ?? 0);

        // Same formula, як ти вже використовуєш у calcMonthlyByFormula
        const totalCostPerMonth =
            consumptionNum > 0
                ? (consumptionNum * energyPriceGross) / 12 + baseFeeGross
                : baseFeeGross;

        const totalCostPerYear = totalCostPerMonth * 12;

        const nameExtern =
            this.currentTariffName ||
            application.tariffNameExternal ||
            application.tariffName ||
            '';

        return {
            tariff: {
                periodStart: application.periodStart ?? null,
                periodEnd: application.periodEnd ?? null,

                id: application.tariffId,
                nameExtern,
                name: nameExtern,

                energyPriceNet,
                energyPriceTax,
                energyPriceGross,

                baseFeeNet,
                baseFeeTax,
                baseFeeGross,

                boni: Array.isArray(application.bonusApplications)
                    ? application.bonusApplications
                    : [],

                cancellationTerm: application.cancellationTerm ?? null,
                cancellationTermUnit: application.cancellationTermUnit ?? null,
                cancellationTermTo: application.cancellationTermTo ?? null,

                priceGuarantyFor:
                    application.priceGuarantyForInMonth ??
                    application.priceGuarantyFor ??
                    null,
                priceGuarantyEnd: application.priceGuarantyEnd ?? null,

                runtimeInMonth: application.runtimeInMonth ?? null,
                renewalInMonth: application.renewalInMonth ?? null,

                // Optional / not provided by getTariffApplication
                portalInfoAdditionalPriceDetails: null,
                portalInfoAdditionalTariffDescription: null,
                tariffType: '-current',
                contractType: null
            },

            priceCalculation: {
                advancePayPlanHeight: null,
                advancePayPlanHeightNet: null,

                totalCostPerMonth,
                totalCostPerMonthNet: null,
                totalCostPerYear,
                totalCostPerYearNet: null,

                totalCostPerMonthInFirstYear: totalCostPerMonth,
                totalCostPerMonthInFirstYearNet: null,
                totalCostPerYearInFirstYear: totalCostPerYear,
                totalCostPerYearInFirstYearNet: null,

                totalSavingsPerYear: 0,
                totalSavingsPerYearNet: 0,

                regionalPricePeriodStart: application.periodStart ?? null
            },

            // Ми нічого не маємо – хай буде null, як у новому тарифі
            customerPriceCalculation: null,

            // Мінімальний stub, щоб не падали місця, де ділять на totalChargesPerKwH
            charges: {
                chargesEntries: [],
                totalChargesPerYear: 0,
                totalChargesPerYearUnit: 'EURO',
                totalChargesPerKwH: 0,
                totalChargesPerKwHUnit: 'CENT_KWH'
            },

            additionalCalculationData: []
        };
    }

    // -----------------------------
    // Main LP flow
    // -----------------------------
    async preloadComparison() {
        if (!this.assertUrlParamsOrAbort(this.urlParams)) return;

        this.showLoader();
        this.loading = true;

        try {
            const { VNR, BP, KWH } = this.urlParams;

            // 1) Get filtered contract data via WP AJAX (no tariffId here)
            const contract = await this.getFilteredContract(BP, VNR);

            const address = contract.address || {
                street: '',
                housnumber: '',
                postalCode: '',
                city: '',
            };

            const state = contract.state || '';
            const currentTariffName = contract.tariffName || '';

            this.contractData = contract;
            this.currentAddress = address;
            this.currentTariffName = currentTariffName;

            // 2) BP data will be securely validated on submit
            this.bpData = { dateOfBirth: '', email: '' };

            // 3) state check
            if (state === 'ENDED' || state === 'CANCELED') {
                this.renderApiError('Dein Vertrag ist ist bereits gekündigt und kann nicht gewechselt werden, bitte schließe einen neuen Vertrag ab');
                return;
            }

            if (state !== 'GPKE_APPROVED') {
                this.renderApiError('Es gibt ein Problem mit Deinem Vertrag, bitte wende dich an den Kundenservice');
                return;
            }

            // 4) Resolve tariffInfo objects for unified renderer
            const currentTariffInfo = await this.resolveCurrentTariffInfo(KWH);
            const newTariffInfo = await this.resolveNewTariffInfo(KWH);

            // 5) Render unified overview via TariffRenderer
            await this.renderTariffOverview({
                currentTariffInfo,
                newTariffInfo,
                city: address.city,
                postalcode: address.postalCode,
                consumption: KWH
            });

            this.initOrDestroyTariffCardsSlider();

        } catch (e) {
            console.error(e);
            this.renderApiError('Leider ist etwas schiefgelaufen. Bitte versuche es erneut.');
        } finally {
            // Show form if not aborted
            this.hideLoader();

            this.loading = false;
        }
    }

    formatDateDdMmYyyy(date) {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}.${month}.${year}`;
    }

    // -----------------------------
    // Submit hook
    // -----------------------------
    handleFormSubmit(e) {
        e.preventDefault();
        if (this.loading || this._abort) return;

        // Clear previous error
        $(SELECTORS.API_ERROR_MESSAGE_SUBMIT).addClass('-hidden').html('');

        const { VNR, BP, KWH } = this.urlParams;

        const dateOfBirth = this.getDateOfBirthInputValue();
        const newTariffStartDate = this.getNewTariffStartDate();
        const newTariffStartIso = newTariffStartDate.toISOString();

        this.loading = true;

        const ensureContract = () => {
            if (this.contractData && this.currentAddress) {
                return Promise.resolve(this.contractData);
            }

            return this.getFilteredContract(BP, VNR).then(contract => {
                this.contractData = contract;

                this.currentAddress = contract.address || {
                    street: '',
                    housnumber: '',
                    postalCode: '',
                    city: '',
                };

                this.currentTariffName = contract.tariffName || '';
                return contract;
            });
        };

        const validateDobAndGetBp = () => {
            return this.getFilteredBusinessPartner(BP, dateOfBirth).then(bpRes => {
                // Store only what we need later
                this.bpData = {
                    dateOfBirth: bpRes?.dateOfBirth || '',
                    email: bpRes?.email || ''
                };

                return bpRes;
            });
        };

        const buildAndSendPayloads = () => {
            if (this.totalCostPerMonth === null) {
                throw 'Tarifdaten konnten nicht ermittelt werden.';
            }

            const heightInEuro = this.totalCostPerMonth;

            const executeTariffChangePayload = {
                communicationType: 'EMAIL',
                contractNumber: VNR,
                // emailAddress: this.bpData?.email || '',
                newTariffId: COMPARISON.NEW_TARIFF_ID,
                newTariffStart: newTariffStartIso
            };

            // if (!executeTariffChangePayload.emailAddress) {
            //     throw 'E-Mail konnte nicht ermittelt werden.';
            // }

            const updatePayPlanPayload = {
                contractNumber: VNR,
                heightInEuro: heightInEuro,
                startDate: newTariffStartIso,
                updateReasonId: 3
            };

            return this._requestProxy('executeTariffChange', executeTariffChangePayload)
                .then((executeRes) => {
                    const execErrors = this.collectErrorMessagesFromResult(executeRes);

                    if (execErrors.length > 0) {
                        const err = new Error('executeTariffChange business error');
                        err._apiErrors = execErrors;
                        throw err;
                    }

                    return this._requestProxy('updatePayPlan', updatePayPlanPayload)
                        .then((updateRes) => {
                            const updErrors = this.collectErrorMessagesFromResult(updateRes);

                            if (updErrors.length > 0) {
                                const err = new Error('updatePayPlan business error');
                                err._apiErrors = updErrors;
                                throw err;
                            }

                            return { executeRes, updatePayPlanRes: updateRes };
                        });
                });
        };

        ensureContract()
            .then(validateDobAndGetBp)
            .then(buildAndSendPayloads)
            .then(() => {
                const newTariffStartFormatted = this.formatDateDdMmYyyy(newTariffStartDate);

                const redirectUrl = `/f%C3%BC%C3%BC%C3%BCrsorge-success/?date=${encodeURIComponent(newTariffStartFormatted)}`;
                console.log(redirectUrl);
                window.location.href = redirectUrl;
            })
            .catch(err => {
                console.error(err);

                const apiErrors = Array.isArray(err?._apiErrors) ? err._apiErrors : [];

                if (apiErrors.length > 0) {
                    const html = apiErrors
                        .map(msg => this.escapeHtml(msg))
                        .join('<br>');

                    $(SELECTORS.API_ERROR_MESSAGE_SUBMIT)
                        .html(html)
                        .removeClass('-hidden');
                } else {
                    const msg = this.normalizeSubmitErrorMessage(err);
                    $(SELECTORS.API_ERROR_MESSAGE_SUBMIT).html(this.escapeHtml(msg)).removeClass('-hidden');
                }
            })
            .then(() => {
                // runs after success OR after catch (including business-error case)
                this.loading = false;
            });
    }

    // -----------------------------
    // Events
    // -----------------------------
    initializeEventListeners() {
        // Disable submit while loading
        this.onLoadingChange = (isLoading) => {
            if (!$(SELECTORS.SUBMIT_BUTTON).length) return;

            if (isLoading) {
                $(SELECTORS.SUBMIT_BUTTON).prop('disabled', true).addClass('-loading');
            } else {
                $(SELECTORS.SUBMIT_BUTTON).prop('disabled', false).removeClass('-loading');
            }
        };

        $(document).on('click', SELECTORS.NEW_TARIFF_CTA, (e) => {
            e.preventDefault();

            const $form = $(SELECTORS.FORM_CONTAINER);

            this.scrollIntoView($form);
        });

        let tariffResizeRaf = null;

        $(window).on('resize.changeTariffSwiper', () => {
            this.initOrDestroyTariffCardsSlider();
        });

    }

    // -----------------------------
    // UI helpers
    // -----------------------------
    collectErrorMessagesFromResult(res) {
        // Normalize to array of items
        const items = Array.isArray(res)
            ? res
            : Array.isArray(res?.response)
                ? res.response
                : Array.isArray(res?.data)
                    ? res.data
                    : [];

        return items
            .filter(item => item && typeof item.errorMessage === 'string')
            .map(item => item.errorMessage.trim())
            .filter(msg => msg.length > 0);
    }

    escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, (ch) => {
            switch (ch) {
                case '&': return '&amp;';
                case '<': return '&lt;';
                case '>': return '&gt;';
                case '"': return '&quot;';
                case '\'': return '&#39;';
                default: return ch;
            }
        });
    }

    showLoader() {
        $(SELECTORS.LOADER).show();
        $(SELECTORS.FORM_WRAPPER).addClass('-hidden');
    }

    hideLoader() {
        $(SELECTORS.LOADER).hide();

        if (!this._abort) {
            $(SELECTORS.FORM_WRAPPER).removeClass('-hidden');
        }
    }

    renderApiError(msg) {
        this._abort = true;
        $(SELECTORS.API_ERROR_MESSAGE_GLOBAL).html(msg).removeClass('-hidden');
    }

    scrollIntoView($el, onDone = null) {
        if (!$el || !$el.length) return;

        const $customerSwitcher = $('.customer-switcher');
        const headerH = ($('header').outerHeight() || 0) + ($customerSwitcher.length ? $customerSwitcher.outerHeight() : 0);

        $('html, body').animate(
            { scrollTop: $el.offset().top - headerH },
            300,
            function () {
                if (typeof onDone === 'function') onDone();
            }
        );
    }

}

// Init
const changeTariffApiService = new ChangeTariffApiService();
export default changeTariffApiService;
